import { useState } from 'react'
import { Link } from 'react-router-dom'

import type { CotizacionDeAlistamiento } from '@/lib/supabase/tipos-bd'
import { Aviso } from '@/shared/ui/Aviso/Aviso'
import { Cargando } from '@/shared/ui/Estados/Cargando'
import { fecha, fechaHora, pesos } from '@/shared/utilidades/formato'

import { ListaDeOrdenes } from './ListaDeOrdenes'
import { PlanillaDeCotizacion } from './PlanillaDeCotizacion'
import { useAlistamientos } from './useAlistamientos'
import estilos from './PaginaAlistamientos.module.css'

/**
 * BODEGA → ALISTAMIENTOS (0246, 0248).
 *
 * Johan: «es lo que llega a WhatsApp… la idea es que todo quede atado a una
 * ODS», y después, viendo las tarjetas: «está súper feo… necesitamos es que
 * ahí separe lo que hay de lo que no hay».
 *
 * Es el escritorio de bodega, así que va como uno: a la izquierda las
 * órdenes, a la derecha la que se está trabajando. Arriba de la orden, sus
 * cotizaciones como pestañas; debajo, la planilla de la elegida para marcar
 * lo que no hay y separarla. En el teléfono, primero la lista y, al tocar una
 * orden, su planilla con un «‹ Órdenes» para volver.
 */
/*
 * DOS PILAS, NO CUATRO (Johan: «no necesito ahí los que esperan al cliente;
 * ahí me deben salir ya los listos, y cuando pasa a listos, literal ya pasa
 * con FEV», y «al otro lado, donde dice facturadas, que me muestre el
 * histórico agrupado por ODS»).
 *   · Para alistar: toda cotización viva que bodega todavía no pasa a FEV,
 *     en la etapa que sea —también las enlazadas sin aceptar (0250)—.
 *   · Listos · FEV: lo que bodega ya pasó a FEV (0252) —esperando a World
 *     Office, en la cola o ya facturado—, agrupado por orden de servicio.
 *     De aquí la reclama el worker que la convierte en factura.
 */
type Filtro = 'alistar' | 'listos'

const FILTROS: [Filtro, string][] = [
  ['alistar', 'Para alistar'],
  ['listos', 'Listos · FEV y remisión'],
]

// Sin IVA también pasa por bodega (0272): llega aquí cuando la pasan a FEV,
// que para ella es solo en la app. Y la que no lleva material, aceptada, va
// de una: bodega no tiene nada que alistar (allí se pasa a FEV).
const yaEsFev = (c: CotizacionDeAlistamiento) =>
  c.facturada || c.fev !== null || (c.renglones.length === 0 && c.estado === 'aprobada')

const cumple = (c: CotizacionDeAlistamiento, f: Filtro) =>
  f === 'alistar' ? !yaEsFev(c) : yaEsFev(c)

export function PaginaAlistamientos() {
  const { data: ordenes = [], isLoading, error } = useAlistamientos()
  const [filtro, setFiltro] = useState<Filtro>('alistar')
  const [buscar, setBuscar] = useState('')
  const [elegida, setElegida] = useState<string | null>(null)
  const [pestana, setPestana] = useState<string | null>(null)
  const [hecho, setHecho] = useState<string | null>(null)

  if (isLoading) return <Cargando mensaje="ALISTANDO CIRCUITOS PENDIENTES..." />
  if (error) return <Aviso tipo="error">{(error as Error).message}</Aviso>

  const cuantas = (f: Filtro) =>
    ordenes.reduce((s, o) => s + o.cotizaciones.filter((c) => cumple(c, f)).length, 0)

  const t = buscar.trim().toLowerCase()
  const visibles = ordenes
    .map((o) => ({ ...o, cotizaciones: o.cotizaciones.filter((c) => cumple(c, filtro)) }))
    .filter((o) => o.cotizaciones.length > 0)
    .filter(
      (o) =>
        !t ||
        `${o.numero} ${o.cliente ?? ''} ${o.cotizaciones.map((c) => c.numero).join(' ')}`
          .toLowerCase()
          .includes(t),
    )

  // En un escritorio ancho se abre sola la primera: una pantalla vacía a la
  // derecha no le sirve a nadie. En el teléfono, primero la lista.
  const ancho = typeof window !== 'undefined' && window.matchMedia('(min-width: 861px)').matches
  const orden =
    visibles.find((o) => o.orden_servicio_id === elegida) ??
    (ancho && elegida === null ? visibles[0] ?? null : null)
  const cot = orden
    ? orden.cotizaciones.find((c) => c.cotizacion_id === pestana) ?? orden.cotizaciones[0]
    : null

  return (
    <div className={estilos.pagina}>
      <header className={estilos.cabecera}>
        <div className={estilos.filtros} role="group" aria-label="Qué ver">
          {FILTROS.map(([valor, texto]) => (
            <button
              key={valor}
              type="button"
              className={filtro === valor ? estilos.filtroVivo : undefined}
              aria-pressed={filtro === valor}
              onClick={() => {
                setFiltro(valor)
                setElegida(null)
              }}
            >
              {texto}
              <span className={estilos.cuenta}>{cuantas(valor)}</span>
            </button>
          ))}
        </div>
        <input
          className={estilos.buscador}
          type="search"
          placeholder="Cliente, OS-… o cotización"
          value={buscar}
          onChange={(e) => setBuscar(e.target.value)}
          aria-label="Buscar"
        />
      </header>

      {hecho && (
        <p className={estilos.listo}>
          {hecho}
          <button type="button" onClick={() => setHecho(null)} aria-label="Cerrar aviso">
            ×
          </button>
        </p>
      )}

      {visibles.length === 0 ? (
        <p className={estilos.vacio}>
          {filtro === 'alistar'
            ? 'Nada por alistar. Cada cotización que se arme o se enlace en una orden aparece aquí.'
            : 'Todavía nada pasó a FEV. Lo que bodega alista y pasa a FEV queda aquí, por orden.'}
        </p>
      ) : (
        <div className={`${estilos.escritorio} ${orden ? estilos.conOrden : ''}`}>
          <aside className={estilos.columnaLista}>
            <ListaDeOrdenes
              ordenes={visibles}
              elegida={orden?.orden_servicio_id ?? null}
              onElegir={(id, cotizacionId) => {
                setElegida(id)
                setPestana(cotizacionId ?? null)
              }}
            />
          </aside>

          <section className={estilos.columnaDetalle}>
            {!orden || !cot ? (
              <div className={estilos.ayuda}>
                <p className={estilos.ayudaTitulo}>Elige una orden</p>
                <ol>
                  <li>Abre la cotización y revisa material por material.</li>
                  <li>
                    Escribe <b>cuánto hay</b> de cada uno. La app arranca con lo que dice el
                    inventario y calcula lo que falta.
                  </li>
                  <li>
                    Si está completa, <b>pásala a FEV</b>. Si falta algo, <b>sepárala</b>: lo que
                    hay pasa a FEV y lo que falta nace en una COT nueva de la misma orden, que
                    espera a que llegue el material.
                  </li>
                </ol>
              </div>
            ) : (
              <>
                <button
                  type="button"
                  className={estilos.volverLista}
                  onClick={() => setElegida(null)}
                >
                  ‹ Órdenes
                </button>
                <header className={estilos.detalleCabecera}>
                  <p className={estilos.detalleTitulo}>
                    <Link
                      to={`/ordenes/${orden.orden_servicio_id}`}
                      className={estilos.detalleNumero}
                    >
                      {orden.numero}
                    </Link>
                    {orden.cliente ?? 'Sin cliente'}
                  </p>
                  <p className={estilos.detalleMeta}>
                    <b>{orden.tecnico ?? 'Sin técnico'}</b>
                    {orden.programada && ` · ${fechaHora(orden.programada)}`}
                    {orden.direccion && ` · ${orden.direccion}`}
                  </p>
                </header>

                {/* Las cotizaciones de la orden, como pestañas. */}
                <div className={estilos.pestanas} role="tablist">
                  {orden.cotizaciones.map((c) => (
                    <button
                      key={c.cotizacion_id}
                      type="button"
                      role="tab"
                      aria-selected={c.cotizacion_id === cot.cotizacion_id}
                      className={
                        c.cotizacion_id === cot.cotizacion_id ? estilos.pestanaViva : undefined
                      }
                      onClick={() => setPestana(c.cotizacion_id)}
                    >
                      {c.numero}
                      <span>{pesos(Number(c.valor ?? 0))}</span>
                    </button>
                  ))}
                </div>

                <div className={estilos.cotEstado}>
                  <Estado c={cot} />
                  {cot.separada_de && (
                    <span className={estilos.chip}>Separada de {cot.separada_de}</span>
                  )}
                  {cot.es_adicional && <span className={estilos.chip}>Adicional</span>}
                  <Envio c={cot} />
                </div>

                {/* La key reinicia las marcas al cambiar de cotización. Sin
                    material también: allí está el paso a FEV. */}
                <PlanillaDeCotizacion
                  key={cot.cotizacion_id}
                  cotizacion={cot}
                  ordenServicioId={orden.orden_servicio_id}
                  onHecho={setHecho}
                />
              </>
            )}
          </section>
        </div>
      )}
    </div>
  )
}

function Estado({ c }: { c: CotizacionDeAlistamiento }) {
  if (c.facturada || c.fev?.estado === 'facturada')
    return (
      <span className={`${estilos.chip} ${estilos.chipFacturada}`}>
        Facturada{c.fev?.numero ? ` · ${c.fev.numero}` : ''}
      </span>
    )
  // 0296: decidida como remisión.
  if (c.fev?.documento === 'remision')
    return c.fev.estado === 'error' ? (
      <span className={`${estilos.chip} ${estilos.chipError}`}>Remisión con error</span>
    ) : (
      <span className={`${estilos.chip} ${estilos.chipFacturada}`}>
        {c.fev.factura?.estado === 'facturada'
          ? `Facturada${c.fev.factura.numero ? ` · ${c.fev.factura.numero}` : ''}`
          : c.fev.estado === 'remitida'
          ? `Remitida${c.fev.numero ? ` · ${c.fev.numero}` : ''}`
          : 'Lista para remisión'}
      </span>
    )
  if (c.fev?.estado === 'error')
    return <span className={`${estilos.chip} ${estilos.chipError}`}>FEV con error</span>
  if (c.fev)
    return <span className={`${estilos.chip} ${estilos.chipFacturada}`}>Lista para FEV</span>
  if (c.documento === 'remision' && !c.facturada)
    return <span className={`${estilos.chip} ${estilos.chipAceptada}`}>Va a remisión</span>
  if (c.en_camino)
    return <span className={`${estilos.chip} ${estilos.chipCamino}`}>En camino a World Office</span>
  if (c.estado === 'aprobada')
    return (
      <span className={`${estilos.chip} ${estilos.chipAceptada}`}>Aceptada por el cliente</span>
    )
  return <span className={`${estilos.chip} ${estilos.chipEspera}`}>Espera al cliente</span>
}

function Envio({ c }: { c: CotizacionDeAlistamiento }) {
  if (!c.envio) return <span className={estilos.envio}>Sin WhatsApp a bodega</span>
  if (c.envio.estado === 'error')
    return (
      <span className={`${estilos.envio} ${estilos.envioError}`} title={c.envio.error ?? undefined}>
        El WhatsApp a bodega falló
      </span>
    )
  return (
    <span className={estilos.envio}>
      {c.envio.estado === 'enviado'
        ? `WhatsApp a bodega ✓ ${c.envio.enviado_en ? fecha(c.envio.enviado_en) : ''}`
        : 'WhatsApp a bodega en cola'}
    </span>
  )
}
